import React, { useState, useEffect, useRef, ImgHTMLAttributes } from 'react';

interface LazyImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'srcSet'> {
  src: string;
  srcSet?: string;
  alt: string;
  placeholder?: string;
  fallback?: string;
  sizes?: string;
  onError?: () => void;
  onLoad?: () => void;
  /**
   * Enable intersection observer for lazy loading
   * @default true
   */
  lazy?: boolean;
  /**
   * Root margin for intersection observer (e.g., '50px')
   * @default '50px'
   */
  rootMargin?: string;
}

/**
 * Optimized Image Component with Lazy Loading
 * 
 * Features:
 * - Native lazy loading with intersection observer fallback
 * - Responsive images with srcset support
 * - Error handling with fallback images
 * - Loading placeholder support
 * - Performance optimized
 */
const LazyImage: React.FC<LazyImageProps> = ({
  src,
  srcSet,
  alt,
  placeholder,
  fallback,
  sizes,
  className = '',
  lazy = true,
  rootMargin = '50px',
  onError,
  onLoad,
  ...rest
}) => {
  const [imageSrc, setImageSrc] = useState<string | null>(placeholder || null);
  const [imageSrcSet, setImageSrcSet] = useState<string | undefined>(undefined);
  const [isLoaded, setIsLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Load image when it comes into view (if lazy loading is enabled)
  useEffect(() => {
    if (!lazy) {
      // Load immediately if lazy loading is disabled
      setImageSrc(src);
      setImageSrcSet(srcSet);
      return;
    }

    const img = imgRef.current;
    if (!img) return;

    // Check if browser supports native lazy loading
    const supportsNativeLazy = 'loading' in HTMLImageElement.prototype;

    if (supportsNativeLazy) {
      // Use native lazy loading
      setImageSrc(src);
      setImageSrcSet(srcSet);
    } else {
      // Use Intersection Observer as fallback
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setImageSrc(src);
              setImageSrcSet(srcSet);
              observerRef.current?.disconnect();
            }
          });
        },
        {
          rootMargin,
        }
      );

      observerRef.current.observe(img);
    }

    return () => {
      observerRef.current?.disconnect();
    };
  }, [src, srcSet, lazy, rootMargin]);

  const handleLoad = () => {
    setIsLoaded(true);
    onLoad?.();
  };

  const handleError = () => {
    if (fallback && imageSrc !== fallback) {
      setImageSrc(fallback);
    }
    onError?.();
  };

  return (
    <img
      ref={imgRef}
      src={imageSrc || undefined}
      srcSet={imageSrcSet}
      sizes={sizes}
      alt={alt}
      className={`${className} ${!isLoaded ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
      loading={lazy ? 'lazy' : 'eager'}
      onLoad={handleLoad}
      onError={handleError}
      {...rest}
    />
  );
};

export default LazyImage;

